import { useFormik } from "formik";
import { useMemo, useState, useRef } from "react";
import { Card, CardBody, Col, Container, Form, Row, Button } from "reactstrap";
import HeaderBox from "../../../DeviceWarrantyRegistrationPage/components/header-box/index";
import validationSchema from "./validationSchema";
import Input from "../../../../components/Common/Input";
import { getUserList, getUser, getUserByEmail } from "../../../../api/user";
import { toastError, toastSuccess } from "../../../../helpers/toast-helper";
import { getResponseData, getResponseItemData } from "../../../../helpers/response-helper";
import { TSelectOption } from "../../../../common/types/common";
import { TUser } from "../../../../common/types/user";
import AsyncSelect from "react-select/async";
import NewUserModal from "../new-user-modal";
import { useUserCreate } from "../../../../common/hooks/api/user";

interface Props {
    onNext: (data: { selectedOwner: TUser; deviceSerialNumber: string }) => void;
}

const OwnerDeviceSelection = ({ onNext }: Props) => {
    const [selectedOwner, setSelectedOwner] = useState<TUser | null>(null);
    const [isNewUserModalOpen, setIsNewUserModalOpen] = useState(false);
    const [selectKey, setSelectKey] = useState(0); // Key to force refresh of AsyncSelect
    const [selectedOption, setSelectedOption] = useState<TSelectOption | null>(null);
    const selectRef = useRef<any>(null);
    const { mutate: createNewUser, isPending: isCreatingUser } = useUserCreate();

    const form = useFormik({
        initialValues: {
            ownerId: "",
            deviceSerialNumber: "",
        },
        validationSchema,
        validateOnMount: true,
        onSubmit: () => {
            if (!selectedOwner) {
                form.setFieldError("ownerId", "Please select an owner");
                return;
            }

            onNext({
                selectedOwner,
                deviceSerialNumber: form.values.deviceSerialNumber,
            });
        },
    });

    const loadOwnerOptions = async (inputValue: string): Promise<TSelectOption[]> => {
        try {
            const userList = await getUserList({
                pagination: { pageSize: 100, pageIndex: 1 },
                sorting: { sortBy: undefined, isDescending: undefined },
                filtering: { searchTerm: inputValue || "" }, // still can help server-side if it does anything
            });

            const { data: users = [] } = getResponseData(userList);

            const norm = (s?: string) => (s ?? "").trim().toLowerCase();

            const search = norm(inputValue);

            const filtered = search
                ? users.filter((u: TUser) => {
                    // Combine First Name + Last Name + Email into a single searchable string
                    const combinedSearchString = [
                        u.firstName,
                        u.lastName,
                        u.email
                    ].filter(Boolean).join(" ").toLowerCase();
                    
                    // Also check displayName separately if it exists
                    const displayNameLower = norm(u.displayName);
                    
                    // Return true if search term is found in combined string or displayName
                    return combinedSearchString.includes(search) || 
                           (displayNameLower && displayNameLower.includes(search));
                })
                : users;

            const options = filtered.map((item: TUser) => {
                const name =
                    item.displayName ||
                    [item.firstName, item.lastName].filter(Boolean).join(" ") ||
                    "Unknown";
                return {
                    value: item.id,
                    label: `${name} (${item.email ?? "no-email"})`,
                };
            });

            return options;
        } catch {
            toastError("Cannot fetch users");
            return [];
        }
    };

    const handleCreateUser = (userData: any) => {
        const userPayload = {
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            displayName: `${userData.firstName} ${userData.lastName}`,
            phone: userData.phone,
            created: new Date().toISOString(),
            ownedDevices: [],
            operatedDevices: [],
            dealerDevices: [],
            appRoles: [],
            company: {
                name: userData.company,
                businessType: "",
                machineApplication: userData.machineApplication,
            },
            address: {
                line1: userData.address,
                line2: userData.address2 || "",
                city: userData.city,
                state: userData.state,
                zip: userData.zip,
                country: "",
            },
            requirePasswordReset: true, // Flag to require password reset on first login
        };

        createNewUser(userPayload, {
            onSuccess: async (response) => {
                if (response?.success) {
                    await toastSuccess("User created successfully. They will be required to reset their password on first login.");
                    
                    // The response might contain the user data directly or we need to fetch it
                    let newUser: TUser | null = null;
                    
                    if (response?.data?.id) {
                        // Fetch the newly created user to get full details
                        try {
                            const userResponse = await getUser(response.data.id);
                            const { data: fetchedUser } = getResponseItemData(userResponse);
                            newUser = fetchedUser;
                        } catch {
                            // Try to use the data from response if available
                            if (response?.data) {
                                newUser = response.data as TUser;
                            }
                        }
                    } else if (response?.data) {
                        // Use the data directly if it's a full user object
                        newUser = response.data as TUser;
                    }
                    
                    if (newUser) {
                        // Create option for the new user
                        const name = newUser.displayName || [newUser.firstName, newUser.lastName].filter(Boolean).join(" ") || "Unknown";
                        const newOption: TSelectOption = {
                            value: newUser.id,
                            label: `${name} (${newUser.email})`,
                        };
                        
                        setSelectedOwner(newUser);
                        setSelectedOption(newOption);
                        form.setFieldValue("ownerId", newUser.id);
                        setIsNewUserModalOpen(false);
                        
                        // Force refresh of AsyncSelect by changing key
                        setSelectKey(prev => prev + 1);
                    } else {
                        // If we can't get the user, try fetching by email
                        try {
                            const userByEmailResponse = await getUserByEmail(userPayload.email);
                            // getUserByEmail returns { success: boolean, data: TUser }
                            if (userByEmailResponse?.success && userByEmailResponse?.data) {
                                const fetchedUser = userByEmailResponse.data as TUser;
                                const name = fetchedUser.displayName || [fetchedUser.firstName, fetchedUser.lastName].filter(Boolean).join(" ") || "Unknown";
                                const newOption: TSelectOption = {
                                    value: fetchedUser.id,
                                    label: `${name} (${fetchedUser.email})`,
                                };
                                
                                setSelectedOwner(fetchedUser);
                                setSelectedOption(newOption);
                                form.setFieldValue("ownerId", fetchedUser.id);
                                setIsNewUserModalOpen(false);
                                
                                // Force refresh of AsyncSelect by changing key
                                setSelectKey(prev => prev + 1);
                            } else {
                                toastError("User created but could not fetch user details. Please search for the user in the list.");
                                setIsNewUserModalOpen(false);
                            }
                        } catch {
                            toastError("User created but could not fetch user details. Please search for the user in the list.");
                            setIsNewUserModalOpen(false);
                        }
                    }
                } else {
                    toastError(response?.errorMessage || "Failed to create user");
                }
            },
            onError: () => {
                toastError("Failed to create user");
            },
        });
    };

    const isButtonDisabled = useMemo(() => !!Object.keys(form.errors).length || !selectedOwner || !form.values.deviceSerialNumber, [form, selectedOwner]);

    return (
        <>
            <HeaderBox title="Select Owner and Device" serialNumber={form.values.deviceSerialNumber || "N/A"} />

            <Card>
                <CardBody>
                    <Container fluid>
                        <Form
                            onSubmit={(e) => {
                                e.preventDefault();
                                form.handleSubmit();
                                return false;
                            }}
                        >
                            <Row className="mb-4">
                                <Col xs={12} className="font-size-18 fw-bold pb-3 text-uppercase">
                                    Owner and Device Selection
                                </Col>
                                <Col xs={12} xl={6}>
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <label className="form-label mb-0">Select Owner</label>
                                            <Button
                                                type="button"
                                                color="success"
                                                size="sm"
                                                onClick={() => setIsNewUserModalOpen(true)}
                                            >
                                                <i className="fas fa-plus me-1"></i>
                                                Create New User
                                            </Button>
                                        </div>
                                        <AsyncSelect
                                            key={selectKey}
                                            ref={selectRef}
                                            cacheOptions
                                            defaultOptions
                                            loadOptions={loadOwnerOptions}
                                            value={selectedOption}
                                            onChange={async (option: TSelectOption | null) => {
                                                if (option) {
                                                    setSelectedOption(option);
                                                    form.setFieldValue("ownerId", option.value);
                                                    form.setFieldTouched("ownerId", true);
                                                    
                                                    // Fetch full user details
                                                    try {
                                                        const userResponse = await getUser(option.value);
                                                        const { data: owner } = getResponseItemData(userResponse);
                                                        setSelectedOwner(owner || null);
                                                    } catch {
                                                        toastError("Cannot fetch user details");
                                                        setSelectedOwner(null);
                                                        setSelectedOption(null);
                                                    }
                                                } else {
                                                    setSelectedOwner(null);
                                                    setSelectedOption(null);
                                                    form.setFieldValue("ownerId", "");
                                                }
                                            }}
                                            onBlur={() => form.setFieldTouched("ownerId", true)}
                                            placeholder="Search and select owner..."
                                            isClearable
                                            styles={{
                                                control: (styles, { isFocused }) => ({
                                                    ...styles,
                                                    borderColor: form.touched.ownerId && form.errors.ownerId ? "#dc3545" : isFocused ? "#80bdff" : "#d9d9d9",
                                                }),
                                            }}
                                        />
                                        {form.touched.ownerId && form.errors.ownerId && (
                                            <div className="invalid-feedback d-block">{form.errors.ownerId}</div>
                                        )}
                                    </div>
                                </Col>
                                <Col xs={12} xl={6}>
                                    <div className="mb-3">
                                        <Input
                                            label="Device Serial Number"
                                            name="deviceSerialNumber"
                                            placeholder="Enter device serial number"
                                            form={form}
                                            value={form.values.deviceSerialNumber}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <div className="text-center mt-3">
                                        <button type="submit" className="btn btn-primary save-user" disabled={isButtonDisabled}>
                                            CONTINUE
                                        </button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </Container>
                </CardBody>
            </Card>

            <NewUserModal
                isOpen={isNewUserModalOpen}
                onToggle={() => setIsNewUserModalOpen(false)}
                onSubmit={handleCreateUser}
                isLoading={isCreatingUser}
            />
        </>
    );
};

export default OwnerDeviceSelection;